import { VideoCameraIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Dialog, Divider } from '@material-ui/core';
import { useForm, Controller } from 'react-hook-form';
import StyledInput from 'shared/components/StyledInput';
import StyledSearchSelect from 'shared/components/StyledSearchSelect';
import TwCheckbox from 'shared/components/TwCheckbox';
import SquaredButton from 'shared/components/SquaredButton';
import { useEffect, useState } from 'react';
import { getMyCompanies } from '@/app/services/Company/companyService';
import { getMySchools, getRoomsBySchool } from '@/app/services/schools/schools';
import { updateCamera } from '@/app/services/camera';
import * as Actions from 'app/store/actions';
import { useDispatch } from 'react-redux';

const EditCameraModal = ({ open, onClose, camera, onSuccess }) => {
	const [searchTimeout, setSearchTimeout] = useState(null);
	const [companiesLoading, setCompaniesLoading] = useState(false);
	const [companies, setCompanies] = useState([]);
	const [schoolsLoading, setSchoolsLoading] = useState(false);
	const [schools, setSchools] = useState([]);
	const [roomsLoading, setRoomsLoading] = useState(false);
	const [rooms, setRooms] = useState([]);
	const dispatch = useDispatch();
	const [submitting, setSubmitting] = useState(false);

	const { control, handleSubmit, reset, watch, setValue } = useForm({
		defaultValues: {
			camera_id: camera?.camera_id || '',
			company: camera?.company ? { value: camera.company.id, label: camera.company.name } : null,
			school: camera?.school ? { value: camera.school.id, label: camera.school.name } : null,
			room_name: camera?.room ? { value: camera.room.id, label: camera.room.name } : null,
			room_id: camera?.room ? { value: camera.room.id, label: camera.room.name } : null,
			admin_only: camera?.admin || false,
			show_in_facelink: camera?.facelink || false,
			show_to_families: camera?.family || false,
		},
	});

	const selectedCompany = watch('company');
	const selectedSchool = watch('school');

	const handleSearchCompanies = async (searchTerm) => {
		if (searchTimeout) {
			clearTimeout(searchTimeout);
		}

		const timeoutId = setTimeout(async () => {
			setCompaniesLoading(true);
			try {
				const res = await getMyCompanies(1, false, searchTerm || '');
				let mappedResults = res.data.data.map((company) => ({
					label: company.name,
					value: company.id,
				}));

				// Ensure selected company is always in the list
				const selectedCompanyValue = watch('company');
				if (selectedCompanyValue) {
					const selectedExists = mappedResults.some((c) => c.value === selectedCompanyValue.value);
					if (!selectedExists) {
						mappedResults = [selectedCompanyValue, ...mappedResults];
					} else {
						// Move selected to top if it exists in results
						const selectedIndex = mappedResults.findIndex((c) => c.value === selectedCompanyValue.value);
						const [selected] = mappedResults.splice(selectedIndex, 1);
						mappedResults.unshift(selected);
					}
				}

				setCompanies(mappedResults);
			} catch (error) {
				console.error('Failed to fetch companies', error);
			}
			setCompaniesLoading(false);
		}, 300);

		setSearchTimeout(timeoutId);
	};

	const handleSearchSchools = async (searchTerm) => {
		if (searchTimeout) {
			clearTimeout(searchTimeout);
		}

		const timeoutId = setTimeout(async () => {
			setSchoolsLoading(true);
			try {
				const res = await getMySchools(searchTerm || '', 1, selectedCompany?.value || null);
				let mappedResults = res.data.data.map((school) => ({
					label: school.name,
					value: school.id,
				}));

				// Ensure selected school is always in the list
				const selectedSchoolValue = watch('school');
				if (selectedSchoolValue) {
					const selectedExists = mappedResults.some((s) => s.value === selectedSchoolValue.value);
					if (!selectedExists) {
						mappedResults = [selectedSchoolValue, ...mappedResults];
					} else {
						// Move selected to top if it exists in results
						const selectedIndex = mappedResults.findIndex((s) => s.value === selectedSchoolValue.value);
						const [selected] = mappedResults.splice(selectedIndex, 1);
						mappedResults.unshift(selected);
					}
				}

				setSchools(mappedResults);
			} catch (error) {
				console.error('Failed to fetch schools', error);
			}
			setSchoolsLoading(false);
		}, 300);

		setSearchTimeout(timeoutId);
	};

	const handleGetRooms = async (schoolId) => {
		if (!schoolId) {
			setRooms([]);
			return;
		}

		setRoomsLoading(true);
		try {
			const res = await getRoomsBySchool(schoolId);
			const mappedResults = res.data.map((room) => ({
				label: room.name,
				value: room.id,
			}));
			setRooms(mappedResults);
		} catch (error) {
			console.error('Failed to fetch rooms', error);
			setRooms([]);
		}
		setRoomsLoading(false);
	};

	const handleCompanyChange = (newValue) => {
		setValue('company', newValue);
		setValue('school', null);
		setValue('room_name', null);
		setRooms([]); // Clear rooms when company changes
	};

	const handleSchoolChange = (newValue) => {
		setValue('school', newValue);
		setValue('room_name', null);
		if (newValue?.value) {
			handleGetRooms(newValue.value);
		} else {
			setRooms([]);
		}
	};

	// Reset school selection when company changes
	useEffect(() => {
		if (!selectedCompany) {
			setSchools([]);
			reset((formValues) => ({
				...formValues,
				school: null,
				room_name: null,
			}));
			return;
		}

		setSchoolsLoading(true);
		getMySchools('', 1, selectedCompany.value)
			.then((res) => {
				const mappedResults = res.data.data.map((school) => ({
					label: school.name,
					value: school.id,
				}));
				setSchools(mappedResults);
			})
			.catch((error) => {
				console.error('Failed to fetch schools for company', error);
				setSchools([]);
			})
			.finally(() => {
				setSchoolsLoading(false);
			});
	}, [selectedCompany]);

	useEffect(() => {
		if (camera) {
			reset({
				camera_id: camera.camera_id || '',
				company: camera.company ? { value: camera.company.id, label: camera.company.name } : null,
				school: camera.school ? { value: camera.school.id, label: camera.school.name } : null,
				room_name: camera.room ? { value: camera.room.id, label: camera.room.name } : null,
				admin_only: camera.admin || false,
				show_in_facelink: camera.facelink || false,
				show_to_families: camera.family || false,
			});

			if (camera.company) {
				setCompanies([{ value: camera.company.id, label: camera.company.name }]);
				handleSearchSchools('');
			}
			if (camera.school) {
				setSchools([{ value: camera.school.id, label: camera.school.name }]);
				handleGetRooms(camera.school.id);
			}
		}
	}, [camera, reset]);

	// Load rooms whenever selected school changes
	useEffect(() => {
		if (selectedSchool?.value) {
			handleGetRooms(selectedSchool.value);
		}
	}, [selectedSchool]);

	const handleAdminOnlyChange = (field, checked) => {
		setValue(field, checked);
		if (checked) {
			// If admin only is checked, uncheck the other two
			setValue('show_in_facelink', false);
			setValue('show_to_families', false);
		}
	};

	const handleOtherCheckboxChange = (field, checked) => {
		setValue(field, checked);
		if (checked) {
			// If either facelink or families is checked, uncheck admin only
			setValue('admin_only', false);
		}
	};

	const onSubmit = (data) => {
		setSubmitting(true);
		const payload = {
			camera_id: data.camera_id,
			company_id: data.company?.value,
			school_id: data.school?.value,
			room_id: data.room_name?.value,
			active: camera?.active || false,
			admin: data.admin_only,
			family: data.show_to_families,
			facelink: data.show_in_facelink,
		};

		updateCamera(camera?.id, payload)
			.then(() => {
				setSubmitting(false);
				dispatch(
					Actions.showMessage({
						message: 'Camera updated successfully',
						variant: 'success',
					})
				);
				onSuccess?.();
				onClose();
			})
			.catch((error) => {
				setSubmitting(false);
				dispatch(
					Actions.showMessage({
						message: error?.response?.data?.message || 'Failed to update camera',
						variant: 'error',
					})
				);
			});
	};

	return (
		<Dialog
			classes={{
				paper: 'w-[90%] max-w-[1000px] m-8 rounded-[12px]',
			}}
			onClose={onClose}
			open={open}
		>
			<form className="rounded-[12px] py-24 px-20 lg:px-[94px] flex flex-col" onSubmit={handleSubmit(onSubmit)}>
				<div className="flex justify-between items-start">
					<div className="flex items-center gap-16">
						<div className="p-12 border rounded-[10px] border-pd-blue">
							<VideoCameraIcon className="size-24 text-pd-blue" />
						</div>
						<div>
							<h2 className="text-18 font-bold">Camera Information</h2>
							<p className="text-[#535862]">Edit camera settings and information.</p>
						</div>
					</div>
					<button
						className="p-8 absolute top-8 right-8 hover:bg-grey-100 rounded-full"
						onClick={onClose}
						type="button"
					>
						<XMarkIcon className="w-24 h-24 text-[#A4A7AE]" />
					</button>
				</div>
				<Divider className="my-20" />

				<div className="flex flex-col mt-20">
					<Controller
						control={control}
						name="camera_id"
						render={({ field }) => <StyledInput label="Camera ID" {...field} />}
					/>
					<Divider className="mt-20 mb-40" />
					<div className="flex flex-row flex-wrap gap-x-24 gap-y-[44px]">
						<Controller
							control={control}
							name="company"
							render={({ field: { value } }) => (
								<StyledSearchSelect
									label="Company"
									loading={companiesLoading}
									onChange={handleCompanyChange}
									onInputChange={(searchTerm) => handleSearchCompanies(searchTerm)}
									options={companies}
									value={value}
									widthClass="w-full md:w-auto"
								/>
							)}
						/>
						<Controller
							control={control}
							name="school"
							render={({ field: { value } }) => (
								<StyledSearchSelect
									label="School"
									loading={schoolsLoading}
									onChange={handleSchoolChange}
									onInputChange={(searchTerm) => handleSearchSchools(searchTerm)}
									options={schools}
									value={value}
									widthClass="w-full md:w-auto"
								/>
							)}
						/>
						<Controller
							control={control}
							name="room_name"
							render={({ field: { onChange, value } }) => (
								<StyledSearchSelect
									label="Room Name"
									loading={roomsLoading}
									onChange={onChange}
									options={rooms}
									value={value}
									widthClass="w-full md:w-auto"
								/>
							)}
						/>
					</div>
				</div>

				<Divider className="my-20" />

				<div className="flex flex-row flex-wrap gap-20">
					<Controller
						control={control}
						name="admin_only"
						render={({ field: { value } }) => (
							<TwCheckbox
								checked={value}
								id="admin_only"
								label="Admin Only"
								onChange={(e) => handleAdminOnlyChange('admin_only', e.target.checked)}
							/>
						)}
					/>
					<Divider flexItem orientation="vertical" />
					<Controller
						control={control}
						name="show_in_facelink"
						render={({ field: { value } }) => (
							<TwCheckbox
								checked={value}
								id="show_in_facelink"
								label="Show in Facelink™"
								onChange={(e) => handleOtherCheckboxChange('show_in_facelink', e.target.checked)}
							/>
						)}
					/>
					<Controller
						control={control}
						name="show_to_families"
						render={({ field: { value } }) => (
							<TwCheckbox
								checked={value}
								id="show_to_families"
								label="Show to Families"
								onChange={(e) => handleOtherCheckboxChange('show_to_families', e.target.checked)}
							/>
						)}
					/>
				</div>
				<div className="flex justify-end gap-16 mt-32">
					<SquaredButton onClick={onClose} size="xl" type="button" variant="outline">
						Cancel
					</SquaredButton>
					<SquaredButton
						disabled={submitting}
						onClick={handleSubmit}
						size="xl"
						type="submit"
						variant="filled"
					>
						Save changes
					</SquaredButton>
				</div>
			</form>
		</Dialog>
	);
};

export default EditCameraModal;
